import { apiClient } from '../../app/api-client';

export interface AvailabilityRow {
  registrarName: string;
  registrarIdno: string;
  siteName: string;
  geographyLabel: string;
  // The code the lot was reported under: any code of the selected medicine's
  // unique medicine (CF-34).
  medicineCode: string;
  lot: string;
  // The last day the lot is available; the precision says whether it was given
  // as that day or as its month.
  lotExpiry: string;
  lotExpiryPrecision: 'DAY' | 'MONTH';
  currentQuantity: number;
  lastReportedAt: string;
}

export interface ImspRegistrar {
  idno: string;
  name: string;
}

// caiet UC92-03 — the institutions the query may be narrowed to. Read from the
// register before the query, because a choice offered only after an answer
// cannot narrow the query that produced it (spec 029 FR-001).
export async function listImspRegistrars(): Promise<ImspRegistrar[]> {
  const { data } = await apiClient.get<{ items: ImspRegistrar[] }>(
    '/stock/availability/registrars',
  );
  return data.items ?? [];
}

export interface ImspRegistrarPage {
  items: ImspRegistrar[];
  total: number;
}

// ACCS-09: the UC92-03 picker reads the list a page at a time, searched on the
// server (docs/shape-ops-stock-check-criteria.md D1).
export async function searchImspRegistrars(input: {
  q: string;
  offset: number;
  limit: number;
}): Promise<ImspRegistrarPage> {
  const { data } = await apiClient.get<ImspRegistrarPage>('/stock/availability/registrars', {
    params: { q: input.q, offset: input.offset, limit: input.limit },
  });
  return { items: data.items ?? [], total: data.total ?? 0 };
}

// CF-52: a Registrar named by a saved address, revalidated by the server against
// the role and the active Registrar. null: not one the caller may query (D2).
export async function findImspRegistrar(idno: string): Promise<ImspRegistrar | null> {
  const { data } = await apiClient.get<ImspRegistrarPage>('/stock/availability/registrars', {
    params: { idno },
  });
  return data.items?.find((item) => item.idno === idno) ?? null;
}

// caiet UC92-01: one medicine, every IMSP Registrar. The reason travels in the
// body because it is journaled with the query; the System Administrator sends an
// empty one and the server accepts it (arbitration D2). `registrarIdno` is
// UC92-03's optional narrowing; '' is the whole IMSP network.
export interface AvailabilityQueryInput {
  medicineCode: string;
  registrarIdno: string;
  reason: string;
}

export async function queryCrossImspAvailability(
  input: AvailabilityQueryInput,
): Promise<AvailabilityRow[]> {
  const { data } = await apiClient.post<{ rows: AvailabilityRow[] }>('/stock/availability/query', {
    medicineCode: input.medicineCode,
    registrarIdno: input.registrarIdno,
    reason: input.reason,
  });
  return data.rows ?? [];
}
